/**
 * When the call-recording setup step counts as done.
 *
 * - Members / Independents: an acceptance of any equivalent policy wording,
 *   OR a "No" — a decline is an answer and is never re-prompted by setup
 *   (they meet the pre-dial gate when they try to call, and can change it in
 *   Menu). A pending admin "Resend consent" re-opens the step either way.
 * - Providers: they read the notice ("I understand" stores ACCEPTED). Never
 *   call-gating.
 */

import {
  hasAcceptedRecordingPolicy,
  hasDeclinedRecordingPolicy,
  type RecordingPolicyConsentFields,
} from '../calls/recording-consent'
import type { RoleProfile } from '../rbac/member-roles'
import { isConsentRequestPending } from './admin-requests'
import { recordingConsentStepVariant } from './requirements'

export type RecordingConsentStepUser = RoleProfile &
  RecordingPolicyConsentFields & { setupRequests?: unknown }

/** Null while the role is unknown. */
export function isRecordingConsentStepDone(
  user: RecordingConsentStepUser | null | undefined,
): boolean | null {
  const variant = recordingConsentStepVariant(user)
  if (!user || !variant) return null
  if (isConsentRequestPending(user)) return false
  if (hasAcceptedRecordingPolicy(user)) return true
  return variant === 'consent' && hasDeclinedRecordingPolicy(user)
}

/**
 * What the account adds on top of each device's own report (server rollup and
 * admin device lines alike). An answer on any device clears the step
 * everywhere. An unanswered Member / Independent never adds it: whether a
 * device asks at all (not while calling is switched off) is that device's
 * call, made in its own report. Providers are always asked, so their account
 * can add it (e.g. right after an admin "ask again").
 */
export function recordingConsentUserSteps(
  user: RecordingConsentStepUser | null | undefined,
): { 'recording-consent': boolean } | undefined {
  const done = isRecordingConsentStepDone(user)
  if (done === true) return { 'recording-consent': true }
  if (done === false && recordingConsentStepVariant(user) === 'acknowledge') return { 'recording-consent': false }
  return undefined
}
